/**
 * Tracks what the app is currently working on, so a crash report says which
 * job failed rather than only where the code broke.
 *
 * Without this, a report cannot distinguish "the code is wrong" from "this
 * device ran out of room for a 24 MP photo" - which is the open question behind
 * every memory-related item in the register.
 *
 * Deliberately metrics only. No file names, no extracted text, no image data:
 * these reports are uploaded automatically when the app crashes, so they must
 * not carry the contents of somebody's document.
 */

export type JobSource =
  | 'image'
  | 'image-collection'
  | 'pdf-direct'
  | 'pdf-pages'
  | 'docx'

export type JobStage =
  | 'preview'
  | 'ocr'
  | 'normalize'
  | 'display'

export interface JobContext {
  source?: JobSource
  stage?: JobStage
  /** Number of files handed to the OCR pipeline. */
  fileCount?: number
  /** Combined size in bytes of those files. */
  totalBytes?: number
  /** MIME type as reported by the browser. */
  fileType?: string
  imageWidth?: number
  imageHeight?: number
  /** Pages found in the source PDF. */
  pageCount?: number
  /** Scale used when rendering PDF pages to canvas. */
  renderScale?: number
  /** Page index being processed when things went wrong. */
  pageIndex?: number
  provider?: string
}

let current: JobContext = {}

/** Merges into the current job. Fields already set are kept unless overwritten. */
export function setJobContext(patch: JobContext): void {
  current = { ...current, ...patch }
}

/** Called when a job finishes or is cancelled, so the next crash is not misattributed. */
export function clearJobContext(): void {
  current = {}
}

export function getJobContext(): JobContext {
  return { ...current }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

/**
 * Heap figures, where the browser exposes them. Chromium only - Safari does not
 * implement performance.memory, so this stays absent on the platform we care
 * most about. Included anyway because it costs nothing and helps on Android.
 */
function describeHeap(): string | null {
  if (typeof performance === 'undefined') {
    return null
  }

  const memory = (performance as Performance & {
    memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number }
  }).memory

  if (!memory) {
    return null
  }

  return `Heap: ${formatBytes(memory.usedJSHeapSize)} used of ${formatBytes(memory.jsHeapSizeLimit)} limit`
}

/** Human-readable block for the crash report. */
export function describeJobContext(): string {
  const job = current
  const lines: string[] = []

  if (job.source) lines.push(`Source: ${job.source}`)
  if (job.stage) lines.push(`Stage: ${job.stage}`)
  if (typeof job.fileCount === 'number') lines.push(`Files: ${job.fileCount}`)
  if (typeof job.totalBytes === 'number') lines.push(`Total size: ${formatBytes(job.totalBytes)}`)
  if (job.fileType) lines.push(`File type: ${job.fileType}`)
  if (typeof job.imageWidth === 'number' && typeof job.imageHeight === 'number') {
    const megapixels = (job.imageWidth * job.imageHeight) / 1_000_000
    lines.push(`Image: ${job.imageWidth}x${job.imageHeight} (${megapixels.toFixed(1)} MP)`)
  }
  if (typeof job.pageCount === 'number') lines.push(`Pages: ${job.pageCount}`)
  if (typeof job.renderScale === 'number') lines.push(`Render scale: ${job.renderScale}`)
  if (typeof job.pageIndex === 'number') lines.push(`Page index: ${job.pageIndex}`)
  if (job.provider) lines.push(`Provider: ${job.provider}`)

  const heap = describeHeap()
  if (heap) {
    lines.push(heap)
  }

  return lines.length > 0 ? lines.join('\n') : 'No job in progress'
}
